import { useState } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import Parse from '../api/config';
import { useAuth } from './AuthContext';
import { useApprovalQueue } from '../hooks/useApprovalQueue';
import {
    Banner,
    Button,
    Card,
    EmptyState,
    LoadingOverlay,
    PageBody,
    PageHeader,
    roleLabel,
} from './ui';

const formatEventDate = (value) => {
    if (!value) return 'Date not set';
    // Bare YYYY-MM-DD must be read as a local date, or it lands a day early.
    const date = /^\d{4}-\d{2}-\d{2}$/.test(value)
        ? new Date(`${value}T00:00:00`)
        : new Date(value);
    return Number.isNaN(date.getTime()) ? 'Date not set' : format(date, 'EEE, MMM d, yyyy');
};

export const Approvals = () => {
    const { user } = useAuth();
    const { items, roles, loading, refresh } = useApprovalQueue();
    const [busy, setBusy] = useState(null);
    const [notice, setNotice] = useState(null);
    const navigate = useNavigate();

    const decide = async (form, role, approved) => {
        let reason = '';
        if (!approved) {
            reason = window.prompt(`Why are you denying "${form.eventName}"? The requester will see this.`) || '';
            if (!reason.trim()) return;
        }

        setBusy(`${approved ? 'Approving' : 'Denying'} ${form.eventName}…`);
        setNotice(null);
        try {
            const fn = approved ? 'approveForm' : 'denyForm';
            const result = await Parse.Cloud.run(fn, {
                formId: form.id,
                role,
                userEmail: user?.email,
                ...(approved ? {} : { reason: reason.trim() }),
            });

            setNotice({
                tone: approved ? 'success' : 'info',
                text:
                    result?.message ||
                    (approved
                        ? `Approved "${form.eventName}" as ${roleLabel(role)}.`
                        : `Denied "${form.eventName}".`),
            });
            await refresh();
        } catch (error) {
            console.error('Approval action failed:', error);
            setNotice({ tone: 'danger', text: `That didn't go through: ${error.message}` });
        } finally {
            setBusy(null);
        }
    };

    return (
        <>
            {busy && <LoadingOverlay message={busy} />}

            <PageHeader
                title="Approvals"
                description={
                    roles.length
                        ? `Requests waiting on you as ${roles.map(roleLabel).join(', ')}.`
                        : 'Requests waiting on your sign-off.'
                }
            />

            <PageBody className="space-y-4">
                {notice && <Banner tone={notice.tone}>{notice.text}</Banner>}

                {loading && !items.length ? (
                    <Card>
                        <EmptyState title="Loading your queue…" />
                    </Card>
                ) : !roles.length ? (
                    <Card>
                        <EmptyState
                            title="You don't hold an approver role"
                            description="Approval roles are assigned in Administrator. Ask an admin to add you if you should be signing off on event requests."
                        />
                    </Card>
                ) : !items.length ? (
                    <Card>
                        <EmptyState
                            title="Nothing needs you right now"
                            description="Every request in your roles has been signed off. New ones will show up here and in your email."
                        />
                    </Card>
                ) : (
                    <ul className="space-y-3">
                        {items.map((form) => (
                            <li key={form.id}>
                                <Card className="p-5">
                                    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                                        <div className="min-w-0">
                                            <h2 className="font-display text-lg font-semibold text-neutral-900">
                                                {form.eventName || 'Untitled event'}
                                            </h2>
                                            <dl className="mt-1.5 flex flex-wrap gap-x-5 gap-y-1 text-sm text-neutral-600">
                                                <div className="flex gap-1.5">
                                                    <dt className="text-neutral-400">When</dt>
                                                    <dd>{formatEventDate(form.date)}</dd>
                                                </div>
                                                <div className="flex gap-1.5">
                                                    <dt className="text-neutral-400">Where</dt>
                                                    <dd>{form.location || '—'}</dd>
                                                </div>
                                                <div className="flex gap-1.5">
                                                    <dt className="text-neutral-400">Department</dt>
                                                    <dd>{form.department || '—'}</dd>
                                                </div>
                                                <div className="flex gap-1.5">
                                                    <dt className="text-neutral-400">Requested by</dt>
                                                    <dd className="truncate">{form.owner || '—'}</dd>
                                                </div>
                                            </dl>
                                            <p className="mt-2.5 text-xs text-neutral-500">
                                                Waiting on you as{' '}
                                                <span className="font-medium text-neutral-700">
                                                    {form.roles.map(roleLabel).join(' and ')}
                                                </span>
                                            </p>
                                        </div>

                                        <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
                                            <Button variant="ghost" onClick={() => navigate(`/home/form/${form.id}`)}>
                                                Review details
                                            </Button>
                                            <Button variant="danger" onClick={() => decide(form, form.roles[0], false)}>
                                                Deny
                                            </Button>
                                            <Button variant="primary" onClick={() => decide(form, form.roles[0], true)}>
                                                Approve
                                            </Button>
                                        </div>
                                    </div>
                                </Card>
                            </li>
                        ))}
                    </ul>
                )}
            </PageBody>
        </>
    );
};
